import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as skillService from '../services/skillService';
import {
  SkillSearchParams, SkillCreateRequest, SkillUpdateRequest,
  ExportPackRequest, ExportScaffoldRequest,
} from '../types/skill';

// ── Queries ───────────────────────────────────────────────────────────────────

export const useSkills = (params?: Partial<SkillSearchParams>) =>
  useQuery(['skills', params], () => skillService.listSkills(params));

export const useSkill = (name: string) =>
  useQuery(['skill', name], () => skillService.getSkill(name), { enabled: !!name });

export const useSkillSources = () =>
  useQuery(['skillSources'], skillService.listSources);

export const usePendingSkills = () =>
  useQuery(['skillsPending'], skillService.listPending);

export const useSkillsForPersona = (personaId: string) =>
  useQuery(
    ['skillsForPersona', personaId],
    () => skillService.getSkillsForPersona(personaId),
    { enabled: !!personaId }
  );

// Uses ChatSession from chatService — session_id is the chat session id
export const useSkillsForSession = (sessionId: string) =>
  useQuery(
    ['skillsForSession', sessionId],
    () => skillService.getSkillsForSession(sessionId),
    { enabled: !!sessionId }
  );

export const useRegistryStats = () =>
  useQuery(['skillRegistryStats'], skillService.getRegistryStats);

// ── Mutations ─────────────────────────────────────────────────────────────────

export const useCreateSkill = () => {
  const qc = useQueryClient();
  return useMutation(
    (req: SkillCreateRequest) => skillService.createSkill(req),
    { onSuccess: () => qc.invalidateQueries(['skills']) }
  );
};

export const useUpdateSkill = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ name, req }: { name: string; req: SkillUpdateRequest }) =>
      skillService.updateSkill(name, req),
    {
      onSuccess: (_, { name }) => {
        qc.invalidateQueries(['skills']);
        qc.invalidateQueries(['skill', name]);
      },
    }
  );
};

export const useDeleteSkill = () => {
  const qc = useQueryClient();
  return useMutation(
    (name: string) => skillService.deleteSkill(name),
    { onSuccess: () => qc.invalidateQueries(['skills']) }
  );
};

export const useAttachToPersona = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ personaId, skillNames }: { personaId: string; skillNames: string[] }) =>
      skillService.attachToPersona(personaId, skillNames),
    {
      onSuccess: (_, { personaId }) =>
        qc.invalidateQueries(['skillsForPersona', personaId]),
    }
  );
};

export const useDetachFromPersona = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ personaId, skillName }: { personaId: string; skillName: string }) =>
      skillService.detachFromPersona(personaId, skillName),
    {
      onSuccess: (_, { personaId }) =>
        qc.invalidateQueries(['skillsForPersona', personaId]),
    }
  );
};

export const useAttachToSession = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ sessionId, skillNames }: { sessionId: string; skillNames: string[] }) =>
      skillService.attachToSession(sessionId, skillNames),
    {
      onSuccess: (_, { sessionId }) =>
        qc.invalidateQueries(['skillsForSession', sessionId]),
    }
  );
};

export const useDetachFromSession = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ sessionId, skillName }: { sessionId: string; skillName: string }) =>
      skillService.detachFromSession(sessionId, skillName),
    {
      onSuccess: (_, { sessionId }) =>
        qc.invalidateQueries(['skillsForSession', sessionId]),
    }
  );
};

export const useSyncSource = () => {
  const qc = useQueryClient();
  return useMutation(
    (sourceId: string) => skillService.syncSource(sourceId),
    { onSuccess: () => qc.invalidateQueries(['skillSources']) }
  );
};

export const useSyncAll = () => {
  const qc = useQueryClient();
  return useMutation(skillService.syncAll, {
    onSuccess: () => qc.invalidateQueries(['skillSources']),
  });
};

export const useApproveSkill = () => {
  const qc = useQueryClient();
  return useMutation(
    (name: string) => skillService.approveSkill(name),
    {
      onSuccess: () => {
        qc.invalidateQueries(['skills']);
        qc.invalidateQueries(['skillsPending']);
      },
    }
  );
};

export const useRejectSkill = () => {
  const qc = useQueryClient();
  return useMutation(
    (name: string) => skillService.rejectSkill(name),
    { onSuccess: () => qc.invalidateQueries(['skillsPending']) }
  );
};

export const useBulkDeleteSkills = () => {
  const qc = useQueryClient();
  return useMutation(
    (skillNames: string[]) => skillService.bulkDeleteSkills(skillNames),
    { onSuccess: () => qc.invalidateQueries(['skills']) }
  );
};

export const useUpdateSource = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ sourceId, data }: { sourceId: string; data: Record<string, any> }) =>
      skillService.updateSource(sourceId, data),
    { onSuccess: () => qc.invalidateQueries(['skillSources']) }
  );
};

export const useRebuildRegistry = () => {
  const qc = useQueryClient();
  return useMutation(skillService.rebuildRegistry, {
    onSuccess: () => qc.invalidateQueries(['skills']),
  });
};
